import { getQuote, resolveVariant, validateSelection } from './lnkbliss-figurine-model.js';

const instances = new WeakMap();
const MAX_PHOTOS = 12;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

function initialize(root) {
  if (instances.has(root)) return;
  const config = JSON.parse(root.querySelector('[data-figurine-config]').textContent);
  const find = (selector) => root.querySelector(selector);
  const form = find('[data-figurine-form]');
  const people = find('[data-people-count]');
  const pets = find('[data-pet-count]');
  const copies = find('[data-copies]');
  const checkout = find('[data-checkout]');
  const addToCart = find('[data-add-to-cart]');
  const purchaseButtons = [checkout, addToCart];
  const picker = find('[data-photo-picker]');
  const dialog = find('[data-preview-dialog]');
  const events = new AbortController();
  let photos = [];
  let busy = false;
  let current = null;
  const listen = (node, event, callback) => node.addEventListener(event, callback, { signal: events.signal });
  const showMessage = (selector, message) => {
    const node = find(selector);
    node.textContent = message;
    node.hidden = !message;
  };
  const money = (cents, currency) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency, currencyDisplay: 'code', minimumFractionDigits: 2,
  }).format(cents / 100);
  const selection = () => {
    const category = form.elements.figurine_category.value;
    return {
      category, form: form.elements.figurine_format.value,
      people: category === 'pets' ? 0 : Number(people.value),
      pets: category === 'people' ? 0 : Number(pets.value), copies: Number(copies.value),
    };
  };
  const describe = (value) => [
    value.people ? `${value.people} ${value.people === 1 ? 'person' : 'people'}` : '',
    value.pets ? `${value.pets} ${value.pets === 1 ? 'pet' : 'pets'}` : '',
  ].filter(Boolean).join(' + ');
  const variantFor = (sku) => {
    const variant = resolveVariant(config.variants, sku);
    return variant && Number.isSafeInteger(variant.price) && variant.price > 0 ? variant : null;
  };
  const formatName = (value) => value.form === 'half' ? 'Half body' : 'Full body';

  function normalizeComposition() {
    const half = form.elements.figurine_format.value === 'half';
    if (half) {
      form.elements.figurine_category.value = 'people';
      people.value = '1';
    }
    const category = form.elements.figurine_category.value;
    root.querySelectorAll('[name="figurine_category"]').forEach((radio) => {
      radio.disabled = half && radio.value !== 'people';
    });
    find('[data-half-note]').hidden = !half;
    find('[data-people-field]').hidden = category === 'pets';
    find('[data-pets-field]').hidden = category === 'people';
    people.disabled = half || category === 'pets';
    pets.disabled = category === 'people';
    if (category === 'mixed' && Number(people.value) + Number(pets.value) > 6) {
      // A category change can bring a previously selected count into a new group.
      pets.value = String(Math.min(Number(pets.value), 5));
      people.value = String(6 - Number(pets.value));
    }
    const peopleMax = half ? 1 : category === 'mixed' ? 6 - Number(pets.value) : 6;
    const petsMax = category === 'mixed' ? 6 - Number(people.value) : 6;
    [...people.options].forEach((option) => { option.disabled = Number(option.value) > peopleMax; });
    [...pets.options].forEach((option) => { option.disabled = Number(option.value) > petsMax; });
  }

  function update() {
    const value = selection();
    const errors = validateSelection(value);
    current = null;
    if (errors.length) {
      showMessage('[data-order-error]', 'Choose a valid number of people, pets, and copies. Copies must be a whole number of at least one.');
      find('[data-order-total]').textContent = '—';
      purchaseButtons.forEach((button) => { button.disabled = true; });
      return;
    }
    const quote = getQuote(value);
    const variant = variantFor(quote.sku);
    const unitPrice = variant?.price ?? quote.unitPriceCents;
    const currency = variant ? config.currency : 'USD';
    if (!Number.isSafeInteger(unitPrice * value.copies)) {
      showMessage('[data-order-error]', 'Please contact us for this quantity.');
      purchaseButtons.forEach((button) => { button.disabled = true; });
      return;
    }
    const fullSingle = variant ? variantFor('FC-FULL-1')?.price : 18800;
    const savings = value.form === 'full' && fullSingle ? Math.max(0, fullSingle * quote.subjectCount - unitPrice) : 0;
    current = { value, quote, variant, unitPrice, currency, total: unitPrice * value.copies };
    const description = `${formatName(value)} · ${describe(value)}`;
    find('[data-price]').textContent = money(unitPrice, currency);
    find('[data-price-average]').textContent = `${money(unitPrice / quote.subjectCount, currency)} per person or pet`;
    find('[data-price-saving]').textContent = savings ? `Save ${money(savings, currency)} compared with individual keepsakes` : 'Personalized just for you';
    find('[data-composition-summary]').textContent = description;
    find('[data-order-description]').textContent = `${description}${value.copies > 1 ? ` × ${value.copies} copies` : ''}`;
    find('[data-order-unit]').textContent = `${money(unitPrice, currency)}${value.copies > 1 ? ' each' : ''}`;
    find('[data-order-total]').textContent = money(current.total, currency);
    for (const format of ['full', 'half']) {
      const available = variantFor(format === 'full' ? 'FC-FULL-1' : 'FC-HALF-1');
      find(`[data-format-price="${format}"]`).textContent = `${format === 'full' ? 'From ' : ''}${money(available?.price ?? (format === 'full' ? 18800 : 14800), available ? config.currency : 'USD')}`;
    }
    showMessage('[data-order-error]', '');
    const canCheckout = config.checkoutEnabled && variant?.id != null;
    purchaseButtons.forEach((button) => { button.disabled = busy || (!config.preview && !canCheckout); });
    checkout.textContent = busy ? 'Adding your keepsake…' : 'Checkout now';
    addToCart.textContent = busy ? 'Adding your keepsake…' : 'Add to cart';
    showMessage('[data-availability-note]', config.preview ? 'Preview mode: both buttons show your selections without adding to the cart or taking payment.' : canCheckout ? '' : 'Online ordering is coming soon. Please contact us below to discuss your keepsake.');
  }

  function renderPhotos() {
    const list = find('[data-photo-list]');
    list.replaceChildren();
    for (const [index, photo] of photos.entries()) {
      const item = document.createElement('li');
      const image = document.createElement('img');
      image.src = photo.url;
      image.alt = `Reference photo ${index + 1}: ${photo.file.name}`;
      const name = document.createElement('span');
      name.textContent = `${index + 1}. ${photo.file.name}`;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.textContent = '×';
      remove.setAttribute('aria-label', `Remove ${photo.file.name}`);
      remove.addEventListener('click', () => {
        URL.revokeObjectURL(photo.url);
        photos = photos.filter((entry) => entry !== photo);
        renderPhotos();
      }, { once: true });
      item.append(image, name, remove);
      list.append(item);
    }
    find('[data-photo-status]').textContent = photos.length ? `${photos.length} reference ${photos.length === 1 ? 'photo' : 'photos'} selected` : 'No photos selected yet';
  }

  listen(picker, 'change', () => {
    const errors = [];
    for (const file of picker.files) {
      if (!PHOTO_TYPES.has(file.type) || file.size === 0) {
        errors.push(`${file.name}: choose a JPG, PNG or WebP photo.`);
      } else if (file.size > MAX_PHOTO_BYTES) {
        errors.push(`${file.name}: each photo must be 10 MB or smaller.`);
      } else if (photos.length >= MAX_PHOTOS) {
        errors.push('You can add up to 12 reference photos.');
      } else if (!photos.some((photo) => photo.file.name === file.name && photo.file.size === file.size && photo.file.lastModified === file.lastModified)) {
        photos.push({ file, url: URL.createObjectURL(file) });
      }
    }
    picker.value = '';
    renderPhotos();
    showMessage('[data-photo-error]', [...new Set(errors)].join(' '));
  });
  root.querySelectorAll('[name="figurine_format"], [name="figurine_category"], [data-people-count], [data-pet-count]').forEach((input) => {
    listen(input, 'change', () => { normalizeComposition(); update(); });
  });
  listen(copies, 'input', update);

  function showPreview(order, action) {
    find('[data-preview-label]').textContent = action === 'cart' ? 'Cart preview' : 'Checkout preview';
    const rows = [
      ['Keepsake', `${formatName(order.value)} · ${describe(order.value)}`],
      ['Copies', String(order.value.copies)],
      ['Reference photos', String(photos.length)],
      ['Engraving', form.elements.engraving.value.trim() || 'None'],
      ['Notes', form.elements.notes.value.trim() || 'None'],
      ['Total', money(order.total, order.currency)],
      ['Included', 'Engraving, gift box, worldwide shipping'],
    ];
    const summary = find('[data-preview-summary]');
    summary.replaceChildren();
    for (const [label, value] of rows) {
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      term.textContent = label;
      description.textContent = value;
      summary.append(term, description);
    }
    dialog.showModal();
  }

  listen(form, 'submit', async (event) => {
    event.preventDefault();
    if (busy) return;
    update();
    if (!current) return;
    if (!photos.length) {
      showMessage('[data-photo-error]', 'Please add at least one reference photo.');
      picker.focus();
      return;
    }
    showMessage('[data-photo-error]', '');
    const order = current;
    const action = event.submitter?.dataset.orderAction === 'cart' ? 'cart' : 'checkout';
    if (config.preview) { showPreview(order, action); return; }
    if (!config.checkoutEnabled || order.variant?.id == null) return;
    const body = new FormData();
    body.append('id', String(order.variant.id));
    body.append('quantity', String(order.value.copies));
    const properties = {
      'Format': formatName(order.value),
      'Subjects': describe(order.value),
      'People': String(order.value.people),
      'Pets': String(order.value.pets),
      'Total subjects': String(order.quote.subjectCount),
      'Engraving': form.elements.engraving.value.trim(),
      'Customization notes': form.elements.notes.value.trim(),
    };
    for (const [name, value] of Object.entries(properties)) if (value) body.append(`properties[${name}]`, value);
    photos.forEach(({ file }, index) => body.append(`properties[Reference photo ${index + 1}]`, file, file.name));
    const controls = [...form.querySelectorAll('input, select, textarea, button')].map((control) => [control, control.disabled]);
    controls.forEach(([control]) => { control.disabled = true; });
    form.setAttribute('aria-busy', 'true');
    busy = true;
    update();
    try {
      const response = await fetch(config.cartAddUrl, { method: 'POST', body, headers: { Accept: 'application/json' }, signal: events.signal });
      const result = await response.json().catch(() => { throw new Error('We could not add your keepsake. Please try again or contact us.'); });
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'We could not add your keepsake. Please try again or contact us.');
      window.location.assign(action === 'cart' ? config.cartUrl : config.checkoutUrl);
    } catch (error) {
      if (events.signal.aborted) return;
      busy = false;
      controls.forEach(([control, disabled]) => { control.disabled = disabled; });
      form.removeAttribute('aria-busy');
      normalizeComposition();
      update();
      showMessage('[data-order-error]', error instanceof TypeError ? 'Please check your connection and try again. Your selections are still here.' : error.message);
    }
  });
  root.querySelectorAll('[data-close-preview]').forEach((button) => listen(button, 'click', () => dialog.close()));
  root.querySelectorAll('[data-gallery-image]').forEach((button) => listen(button, 'click', () => {
    let image = find('[data-gallery-main] img');
    if (!image) {
      find('.mf-photo-slot')?.remove();
      image = document.createElement('img');
      image.className = 'mf-main-image';
      find('[data-gallery-main]').prepend(image);
    }
    image.removeAttribute('srcset');
    image.src = button.dataset.galleryImage;
    image.alt = button.dataset.galleryAlt || 'Hand-finished full-color 3D detail';
    root.querySelectorAll('[data-gallery-image]').forEach((thumbnail) => {
      thumbnail.classList.toggle('is-active', thumbnail === button);
      thumbnail.setAttribute('aria-pressed', String(thumbnail === button));
    });
  }));
  normalizeComposition();
  update();
  instances.set(root, () => {
    events.abort();
    photos.forEach((photo) => URL.revokeObjectURL(photo.url));
    instances.delete(root);
  });
}

const initializeWithin = (scope) => scope.querySelectorAll('[data-figurine-section]').forEach(initialize);
initializeWithin(document);
document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-figurine-section]').forEach((root) => instances.get(root)?.());
});
